import React, { useCallback, useState } from "react";
import _ from "lodash";
import { v4 as uuidv4 } from "uuid";

import { EditIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { useCaching } from "utils/common";
import { IS_TABLE_FIELD_ROW_MOVE_ENABLED } from "utils/constants";
import { format, FORMATS, parseISO } from "utils/datetime";

import Box from "components/Box";
import Button from "components/Button";
import Dialog, { DialogActions, DialogTitle } from "components/Dialog";
import IconButton from "components/IconButton";
import SimpleTable from "components/SimpleTable";
import SmallAlert from "components/SmallAlert";
import Stack from "components/Stack";
import Text from "components/Text";

import { saveTableData } from "entities/Field/components/Tablefield/utils";

interface ITablefieldEditButton {
  field: {
    display_name: string;
    header_name: Array<{ headerName: string; field: string }>;
  };
  initialValue: any[];
  onSave: ({ newValue }: { newValue: any }) => Promise<any> | void;
  onUpdate: ({ message }: { message?: string }) => void;
  lastSavedAt: string | null;
  buttonDataTestid?: string;
}

const TablefieldEditButton: React.FC<ITablefieldEditButton> = ({
  field,
  initialValue,
  onSave,
  onUpdate,
  lastSavedAt,
  buttonDataTestid,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useCaching(initialValue);
  const [saveTimer, setSaveTimer] = useState<NodeJS.Timeout | null>(null);

  const handleEditButtonClick = useCallback(() => setIsOpen(true), []);

  const handleSave = useCallback(() => {
    const saveResult = onSave({
      newValue: value.map((row: any) => _.omit(row, "key")),
    });

    if (typeof saveResult?.then === "function") {
      saveResult.then(() => {
        setIsOpen(false);
        onUpdate({ message: `${field.display_name} successfully updated!` });

        if (!_.isNull(saveTimer)) {
          clearTimeout(saveTimer);
        }
      });
    } else {
      setIsOpen(false);
      onUpdate({ message: `${field.display_name} successfully updated!` });

      if (!_.isNull(saveTimer)) {
        clearTimeout(saveTimer);
      }
    }
  }, [setIsOpen, onSave, value, onUpdate, field.display_name, saveTimer]);

  const handleCancel = useCallback(() => {
    if (!_.isNull(saveTimer)) {
      clearTimeout(saveTimer);
    }

    setValue(initialValue);
    setIsOpen(false);
  }, [setIsOpen, setValue, initialValue, saveTimer]);

  const handleChange = useCallback(
    (newValue) => {
      setValue(newValue);

      // Save the data every 3 seconds of inactivity.
      const newTimer = setTimeout(() => {
        saveTableData({ newValue, onSave, onUpdate });
      }, 3000);

      setSaveTimer((prevTimer) => {
        if (!_.isNull(prevTimer)) {
          clearTimeout(prevTimer);
        }

        return newTimer;
      });
    },
    [onSave, setValue, onUpdate]
  );

  const onAddEmptyRow = useCallback(() => {
    setValue((value: any[]) => {
      const newValue = !_.isEmpty(value)
        ? [...value, { key: uuidv4() }]
        : [{ key: uuidv4() }];

      // Save the data every 3 seconds of inactivity.
      const newTimer = setTimeout(() => {
        saveTableData({ newValue, onSave, onUpdate });
      }, 3000);

      setSaveTimer((prevTimer) => {
        if (!_.isNull(prevTimer)) {
          clearTimeout(prevTimer);
        }

        return newTimer;
      });

      return newValue;
    });
  }, [onSave, setValue, onUpdate]);

  return (
    <>
      <IconButton
        size="small"
        onClick={handleEditButtonClick}
        dataTestid={buttonDataTestid}
      >
        <EditIcon />
      </IconButton>
      {isOpen && (
        <Dialog open={isOpen} maxWidth="md" fullWidth onClose={handleCancel}>
          <Box
            sx={{
              padding: theme.spacing(1),
            }}
            data-testid="table-field-edit-dialog"
          >
            <DialogTitle data-testid="table-field-dialog-title">
              {field.display_name}
            </DialogTitle>
            <Stack spacing={1} sx={{ paddingX: theme.spacing(3) }}>
              <SimpleTable
                rows={value}
                headers={(field.header_name || []).map(
                  ({ headerName, field }) => ({
                    label: headerName,
                    key: field,
                  })
                )}
                onUpdated={handleChange}
                editable
                showDeleteCell
                showMoveCell={IS_TABLE_FIELD_ROW_MOVE_ENABLED}
              />
              <Button
                size="small"
                color="secondary"
                sx={{ alignSelf: "flex-start" }}
                onClick={onAddEmptyRow}
                data-testid="add-new-row-button"
              >
                Add new row
              </Button>
            </Stack>
            <DialogActions
              sx={{
                padding: 2,
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <SmallAlert title="Don’t forget to save your changes after editing!" />
                {!_.isNil(lastSavedAt) && (
                  <Text
                    variant="text3"
                    component="span"
                    sx={{ color: colors.gray80 }}
                    data-testid="last-saved-timestamp"
                  >
                    Last saved at{" "}
                    {format(parseISO(lastSavedAt), FORMATS.FRONTEND.DATETIME)}
                  </Text>
                )}
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button
                  variant="text"
                  onClick={handleCancel}
                  data-testid="cancel-button"
                >
                  Cancel
                </Button>
                <Button onClick={handleSave} data-testid="submit-button">
                  Save
                </Button>
              </Stack>
            </DialogActions>
          </Box>
        </Dialog>
      )}
    </>
  );
};

export default TablefieldEditButton;
